import { z } from 'zod';

import { paginationSchema } from '../shared/schemes.js';

export const glossaryTypeV1Schema = z.enum(['platform', 'channel']);
export const glossaryTypeV2Schema = z.enum([
  'urn:mint:field:platform_v2',
  'urn:mint:field:channel_v2',
]);

export type GlossaryType = z.infer<typeof glossaryTypeV1Schema>;
export type GlossaryTypeV2 = z.infer<typeof glossaryTypeV2Schema>;

export const glossaryAliasSchema = z.object({
  id: z.string(),
  name: z.string(),
});

export type GlossaryAlias = z.infer<typeof glossaryAliasSchema>;

export const glossaryItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  label: z.string().nullable(),
  codes: z.array(glossaryAliasSchema),
});

export type GlossaryItem = z.infer<typeof glossaryItemSchema>;

export const glossaryListResponseSchema = z.object({
  data: z
    .object({
      items: z.array(glossaryItemSchema),
      additional: z.object({
        codesPerGlossary: z.number(),
      }),
    })
    .extend(paginationSchema.shape),
});

export type GlossaryListResponse = z.infer<typeof glossaryListResponseSchema>;
export type GlossaryListQueryResult = GlossaryListResponse['data'];

export const glossaryV1QuerySchema = z.object({
  glossaryType: glossaryTypeV1Schema,
  search: z.string().optional().prefault(''),
  pageNumber: z.number().optional().prefault(1),
  itemsPerPage: z.number().optional(),
});

export const glossaryV2QuerySchema = z.object({
  glossaryType: glossaryTypeV2Schema,
  search: z.string().optional().prefault(''),
  pageNumber: z.number().optional().prefault(1),
  itemsPerPage: z.number().optional(),
});

export const addGlossaryItemV1PayloadSchema = z.object({
  name: z.string(),
  glossaryType: glossaryTypeV1Schema,
});

export type AddGlossaryItemV1Payload = z.infer<
  typeof addGlossaryItemV1PayloadSchema
>;

export const addGlossaryItemResponseSchema = z.object({
  data: z.object({
    additional: z.object({
      id: z.string(),
    }),
  }),
});

export type AddGlossaryItemResponse = z.infer<
  typeof addGlossaryItemResponseSchema
>;

export const addGlossaryItemV2PayloadSchema = z.object({
  name: z.string(),
  glossaryType: glossaryTypeV2Schema,
});

export type AddGlossaryItemV2Payload = z.infer<
  typeof addGlossaryItemV2PayloadSchema
>;

export const BulkGlossaryAliasesUpdatePayloadSchema = z.array(
  z.union([
    z.object({
      name: z.string().trim(),
      action: z.literal('create'),
    }),
    z.object({
      id: z.string(),
      name: z.string().trim(),
      action: z.literal('update'),
    }),
    z.object({
      id: z.string(),
      name: z.string().trim(),
      action: z.literal('delete'),
    }),
  ]),
);

export type BulkGlossaryAliasesUpdatePayload = z.infer<
  typeof BulkGlossaryAliasesUpdatePayloadSchema
>;

export const BulkGlossaryAliasesUpdateResponseSchema = z.array(
  z.object({
    data: z.object({
      id: z.string(),
      name: z.string(),
    }),
    error: z.union([
      z.null(),
      z.object({ type: z.string(), message: z.string() }),
    ]),
  }),
);

export type BulkGlossaryAliasesUpdateResponse = z.infer<
  typeof BulkGlossaryAliasesUpdateResponseSchema
>;
